import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { exchangeCodeForTokens, saveCalendarTokens } from '../../services/calendarService';
import { verifyServerStateToken } from '../../services/authStateService';

function GoogleCalendarCallback() {
  const [status, setStatus] = useState('Processing...');
  const [error, setError] = useState(null);
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    async function handleCallback() {
      try {
        console.log('Google Calendar callback started');
        
        // Check if user is authenticated
        if (!userProfile || !userProfile.company) {
          throw new Error('You must be logged in with a company profile to connect Google Calendar');
        }

        // Create a user-specific session storage key
        const sessionStorageKey = `gcal_callback_processed_${userProfile.uid}`;
        const hasProcessed = sessionStorage.getItem(sessionStorageKey);
        
        // If we've already processed this callback in this session for this specific user, redirect back to settings
        if (hasProcessed === 'true') {
          console.log('Callback already processed in this session for this user, returning to settings');
          setStatus('Already processed, redirecting back...');
          window.location.href = '/app/settings';
          return;
        }
        
        // Extract query parameters from URL
        const queryParams = new URLSearchParams(window.location.search);
        const code = queryParams.get('code');
        const state = queryParams.get('state');
        const error = queryParams.get('error');
        
        // Get the timestamp and hint parameters we added as fallback
        const timestamp = queryParams.get('timestamp');
        const hint = queryParams.get('hint');

        console.log('Callback parameters received:', { 
          state, 
          timestamp,
          hint
        });

        // Check for errors in the callback
        if (error) {
          throw new Error(`Google OAuth error: ${error}`);
        }

        // Validate authorization code
        if (!code) {
          throw new Error('No authorization code received');
        }
        
        // Validate state using server-side verification
        if (!state) {
          throw new Error('No state parameter received');
        }
        
        // Flag to track if we should continue despite verification issues
        let shouldContinue = false;
        let verificationUserId = null;
        
        console.log('Verifying state token with server...');
        try {
          // Verify the state token with the server
          const verificationResult = await verifyServerStateToken(state);
          
          if (!verificationResult.success) {
            // If token is already used but this is a valid callback session with code, we can continue
            if (verificationResult.error === 'State token already used' && code) {
              console.warn('State token already used, but proceeding with authorization code');
              shouldContinue = true;
              // If userId is available, capture it
              if (verificationResult.userId) {
                verificationUserId = verificationResult.userId;
              }
            } else {
              throw new Error(verificationResult.error || 'Invalid state token');
            }
          } else {
            console.log('State token verified successfully');
            verificationUserId = verificationResult.userId;
          }
          
          // Optional: Verify the user ID matches if we have it
          if (verificationUserId && verificationUserId !== userProfile.uid) {
            console.warn('State token belongs to a different user');
            // We could throw an error here, but it might be too strict
            // since the user might have multiple tabs/sessions
          }
        } catch (stateError) {
          console.error('State verification failed:', stateError);
          
          // If the error is "token already used" we can continue with the auth code flow
          // This can happen if the user refreshes the callback page after successful auth
          if (stateError.message && stateError.message.includes('already used') && code) {
            console.warn('Continuing despite state verification failure (token already used)');
            shouldContinue = true;
          } else {
            throw new Error('Security verification failed - please try again');
          }
        }

        if (!shouldContinue && !verificationUserId) {
          throw new Error('Security verification could not be completed');
        }

        // Set the redirect URI (must match what was used in the initial request)
        const redirectUri = `${window.location.origin}/auth/google-calendar-callback`;
        console.log('Using redirect URI:', redirectUri);

        // Exchange the code for access and refresh tokens
        setStatus('Exchanging authorization code for tokens...');
        const tokens = await exchangeCodeForTokens(code, redirectUri);

        // Save the tokens to the company profile
        setStatus('Saving connection to your profile...');
        await saveCalendarTokens(userProfile.company, tokens);

        // Mark as processed in this session for this specific user
        sessionStorage.setItem(sessionStorageKey, 'true');

        // Dispatch event to notify the settings modal
        const connectedEvent = new CustomEvent('google-service-connected', {
          detail: {
            service: 'calendar',
            data: tokens
          }
        });
        window.dispatchEvent(connectedEvent);

        // Success
        setStatus('Successfully connected Google Calendar!');
        
        // Redirect back to settings after a short delay with a full page reload
        setTimeout(() => {
          // Use window.location.href for a full page reload instead of navigate()
          window.location.href = '/app/settings';
        }, 2000);
        
      } catch (err) {
        console.error('Google Calendar callback error:', err);
        setError(err.message || 'Failed to connect Google Calendar');
        
        // Redirect back to settings after a longer delay with a full page reload
        setTimeout(() => {
          // Use window.location.href for a full page reload instead of navigate()
          window.location.href = '/app/settings';
        }, 5000);
      }
    }

    // Only run the callback handler if we have the user profile
    if (userProfile) {
      handleCallback();
    }
  }, [userProfile, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Google Calendar Integration</h2>
          
          {error ? (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-4">
              <p className="font-bold">Error</p>
              <p>{error}</p>
              <p className="mt-2 text-sm">Redirecting you back to settings...</p>
            </div>
          ) : (
            <>
              <div className="flex justify-center mb-4">
                {status === 'Successfully connected Google Calendar!' ? (
                  <svg className="w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                ) : (
                  <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
                )}
              </div>
              
              <p className="text-gray-700 mb-4">{status}</p>
              
              {status === 'Successfully connected Google Calendar!' && (
                <p className="text-sm text-gray-500">Redirecting you back to settings...</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default GoogleCalendarCallback; 